"use client";

import type React from "react";
import {
  Box,
  VStack,
  Link as ChakraLink,
  Image,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  useMediaQuery,
  Tooltip,
} from "@chakra-ui/react";
import { Link, useLocation } from "react-router-dom";
import UserProfileDropdown from "./UserProfileDropdown.tsx";
import { useAppTheme } from "../ThemeContext.tsx";
import { useCreditsGate } from "../CreditsGateContext.tsx";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const location = useLocation();
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const { isCreditsLocked, creditsRemaining } = useCreditsGate();

  const disabledNavReason =
    creditsRemaining != null && creditsRemaining < 0
      ? "Your credit balance is overdrawn. Open Billing to purchase credits before using this section."
      : "You have less than 1 credit remaining. Open Billing to purchase credits or manage your subscription before using this section.";

  const navItems = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: (
        <>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 17.5C5.85775 17.5 2.5 14.1423 2.5 10C2.5 6.6415 4.70725 3.799 7.75 2.8435V4.4365C6.46069 4.96003 5.39335 5.91528 4.73054 7.13884C4.06774 8.36239 3.85066 9.77823 4.11644 11.1442C4.38222 12.5101 5.11434 13.7412 6.18757 14.627C7.2608 15.5128 8.60846 15.9981 10 16C11.1953 16 12.3634 15.643 13.3546 14.9749C14.3458 14.3068 15.115 13.358 15.5635 12.25H17.1565C16.201 15.2928 13.3585 17.5 10 17.5ZM17.4625 10.75H9.25V2.5375C9.49675 2.51275 9.74725 2.5 10 2.5C14.1423 2.5 17.5 5.85775 17.5 10C17.5 10.2527 17.4873 10.5033 17.4625 10.75ZM10.75 4.0465V9.25H15.9535C15.7866 7.92821 15.1846 6.6995 14.2426 5.75743C13.3005 4.81535 12.0718 4.21344 10.75 4.0465Z"
              fill="currentColor"
            ></path>
          </svg>
        </>
      ),
    },
    {
      name: "Agents",
      path: "/agents",
      icon: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M11.125 2.5C11.125 2.83319 10.9802 3.13254 10.75 3.33854V4.75H14.5C15.7427 4.75 16.75 5.75736 16.75 7V14.5C16.75 15.7427 15.7427 16.75 14.5 16.75H5.5C4.25736 16.75 3.25 15.7427 3.25 14.5V7C3.25 5.75736 4.25736 4.75 5.5 4.75H9.25V3.33854C9.01982 3.13254 8.875 2.83319 8.875 2.5C8.875 1.87868 9.3787 1.375 10 1.375C10.6213 1.375 11.125 1.87868 11.125 2.5ZM5.5 6.25C5.08579 6.25 4.75 6.58579 4.75 7V14.5C4.75 14.9142 5.08579 15.25 5.5 15.25H14.5C14.9142 15.25 15.25 14.9142 15.25 14.5V7C15.25 6.58579 14.9142 6.25 14.5 6.25H10.75H9.25H5.5ZM2.5 8.5H1V13H2.5V8.5ZM17.5 8.5H19V13H17.5V8.5ZM7.75 11.875C8.37132 11.875 8.875 11.3713 8.875 10.75C8.875 10.1287 8.37132 9.625 7.75 9.625C7.12868 9.625 6.625 10.1287 6.625 10.75C6.625 11.3713 7.12868 11.875 7.75 11.875ZM12.25 11.875C12.8713 11.875 13.375 11.3713 13.375 10.75C13.375 10.1287 12.8713 9.625 12.25 9.625C11.6287 9.625 11.125 10.1287 11.125 10.75C11.125 11.3713 11.6287 11.875 12.25 11.875Z"
              fill="currentColor"
            ></path>
          </svg>
        </>
      ),
    },
    {
      name: "Knowledge Base",
      path: "/knowledge-base",
      icon: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M3.25 14.875V4.75C3.25 4.15326 3.48705 3.58097 3.90901 3.15901C4.33097 2.73705 4.90326 2.5 5.5 2.5H16C16.1989 2.5 16.3897 2.57902 16.5303 2.71967C16.671 2.86032 16.75 3.05109 16.75 3.25V16.75C16.75 16.9489 16.671 17.1397 16.5303 17.2803C16.3897 17.421 16.1989 17.5 16 17.5H5.875C5.17881 17.5 4.51113 17.2234 4.01884 16.7312C3.52656 16.2389 3.25 15.5712 3.25 14.875ZM15.25 16V13.75H5.875C5.57663 13.75 5.29048 13.8685 5.0795 14.0795C4.86853 14.2905 4.75 14.5766 4.75 14.875C4.75 15.1734 4.86853 15.4595 5.0795 15.6705C5.29048 15.8815 5.57663 16 5.875 16H15.25ZM4.75 12.5028C5.10152 12.3358 5.48586 12.2495 5.875 12.25H15.25V4H5.5C5.30109 4 5.11032 4.07902 4.96967 4.21967C4.82902 4.36032 4.75 4.55109 4.75 4.75V12.5028Z"
              fill="currentColor"
            ></path>
          </svg>
        </>
      ),
    },
    {
      name: "Phone Numbers",
      path: "/phone-numbers",
      icon: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M6.25 2.5H14.5C14.6989 2.5 14.8897 2.57902 15.0303 2.71967C15.171 2.86032 15.25 3.05109 15.25 3.25V16.75C15.25 16.9489 15.171 17.1397 15.0303 17.2803C14.8897 17.421 14.6989 17.5 14.5 17.5H5.5C5.30109 17.5 5.11032 17.421 4.96967 17.2803C4.82902 17.1397 4.75 16.9489 4.75 16.75V1H6.25V2.5ZM6.25 7.75H13.75V4H6.25V7.75ZM6.25 9.25V16H13.75V9.25H6.25Z"
              fill="currentColor"
            ></path>
          </svg>
        </>
      ),
    },
    {
      name: "Call History",
      path: "/call-history",
      icon: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M10 2.5C14.1422 2.5 17.5 5.85775 17.5 10C17.5 14.1422 14.1422 17.5 10 17.5C5.85775 17.5 2.5 14.1422 2.5 10H4C4 13.3135 6.6865 16 10 16C13.3135 16 16 13.3135 16 10C16 6.6865 13.3135 4 10 4C7.9375 4 6.118 5.04025 5.03875 6.625H7V8.125H2.5V3.625H4V5.5C5.368 3.6775 7.54675 2.5 10 2.5ZM10.75 6.25V9.68875L13.1823 12.121L12.121 13.1823L9.25 10.3098V6.25H10.75Z"
              fill="currentColor"
            ></path>
          </svg>
        </>
      ),
    },
    {
      name: "Billing",
      path: "/billing",
      icon: (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M16 17.5H4C3.80109 17.5 3.61032 17.421 3.46967 17.2803C3.32902 17.1397 3.25 16.9489 3.25 16.75V3.25C3.25 3.05109 3.32902 2.86032 3.46967 2.71967C3.61032 2.57902 3.80109 2.5 4 2.5H16C16.1989 2.5 16.3897 2.57902 16.5303 2.71967C16.671 2.86032 16.75 3.05109 16.75 3.25V16.75C16.75 16.9489 16.671 17.1397 16.5303 17.2803C16.3897 17.421 16.1989 17.5 16 17.5ZM15.25 16V4H4.75V16H15.25ZM7 7.75H13V9.25H7V7.75ZM7 10.75H13V12.25H7V10.75Z"
              fill="currentColor"
            ></path>
          </svg>
        </>
      ),
    },
  ];

  const SidebarContent = () => (
    <>
      <Box p={8}>
        <Image
          src={process.env.REACT_APP_DASHBOARD_ICON_URL}
          alt="Dashboard Logo"
          maxWidth="150px"
        />
      </Box>
      <Box p={4} />
      <Box flex="1" overflowY="auto" px={4} pb={4}>
        <VStack align="stretch" spacing={1}>
          {navItems.map((item) => {
            const locked = isCreditsLocked && item.path !== "/billing";
            return locked ? (
              <Tooltip
                key={item.path}
                label={disabledNavReason}
                hasArrow
                placement="right"
                openDelay={200}
                maxW="280px"
                shouldWrapChildren
              >
                <Box
                  py={2}
                  px={4}
                  borderRadius="md"
                  fontWeight="medium"
                  display="flex"
                  alignItems="center"
                  gap={2}
                  opacity={0.45}
                  cursor="not-allowed"
                  userSelect="none"
                  aria-disabled
                  color={isDark ? "whiteAlpha.600" : "gray.500"}
                >
                  {item.icon}
                  {item.name}
                </Box>
              </Tooltip>
            ) : (
              <ChakraLink
                key={item.path}
                as={Link}
                to={item.path}
                py={2}
                px={4}
                borderRadius="md"
                fontWeight="medium"
                display="flex" // added to arrange children in a row
                alignItems="center" // added to vertically center the items
                gap={2} // optional: adds spacing between icon and name
                color={
                  location.pathname === item.path
                    ? "white"
                    : isDark
                    ? "whiteAlpha.900"
                    : "gray.800"
                }
                bg={
                  location.pathname === item.path
                    ? isDark
                      ? "gray.500"
                      : "gray.700"
                    : "transparent"
                }
                _hover={{
                  bg: "gray.700",
                  color: "whiteAlpha.900",
                  textDecoration: "none",
                }}
                transition="all 0.2s"
                onClick={isLargerThan768 ? undefined : onClose}
              >
                {item.icon}
                {item.name}
              </ChakraLink>
            );
          })}
        </VStack>
      </Box>
      <Box
        p={4}
        borderColor={isDark ? "gray.700" : "gray.200"}
        borderTopWidth={1}
      >
        <UserProfileDropdown />
      </Box>
    </>
  );

  if (isLargerThan768) {
    return (
      <Box
        as="nav"
        position="fixed"
        top={0}
        left={0}
        width="230px"
        height="100vh"
        bg={isDark ? "gray.800" : "whiteAlpha.900"}
        boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
        display={isOpen ? "flex" : "none"}
        flexDirection="column"
        zIndex={1000}
        transition="transform 0.3s"
        sx={
          isDark
            ? {
                "&::-webkit-scrollbar": {
                  height: "4px",
                },
                "&::-webkit-scrollbar-button": {
                  display: "none",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#2D3748", // Dark track color
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#4A5568", // Dark thumb color
                  borderRadius: "2px",
                },
                // For Firefox
                scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                _hover: {
                  scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                },
              }
            : {
                "&::-webkit-scrollbar": {
                  height: "4px",
                },
                "&::-webkit-scrollbar-button": {
                  display: "none",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#fff",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#fff",
                  borderRadius: "2px",
                },
                scrollbarColor: "rgb(220, 220, 220) white",
                _hover: {
                  scrollbarColor: "rgb(220, 220, 220) white",
                },
              }
        }
      >
        <SidebarContent />
      </Box>
    );
  }

  return (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
      <DrawerOverlay>
        <DrawerContent bg={isDark ? "gray.800" : "whiteAlpha.900"}>
          <DrawerCloseButton />
          <DrawerBody p={0}>
            <SidebarContent />
          </DrawerBody>
        </DrawerContent>
      </DrawerOverlay>
    </Drawer>
  );
};

export default Sidebar;
